/** @jsx jsx */

import { Fragment as _, useState, useMemo, useEffect } from 'react';
import { jsx } from '@emotion/core';
import EditableText from 'components/lib/EditableText';
import renderBlock from 'services/apiHelpers/renderBlock';
import { Row, Column } from '@chameleoncreator/creator-ui';
import Image from 'components/lib/Image';
import ContinueBtn from 'components/lib/ContinueBtn/continueBtn_v2';
import Container from 'components/Layouts/ContainerLogical';
import Media from 'components/lib/Media';
import Video from 'components/lib/Video';
import SVG from 'components/lib/SVG';
import { interactionContext } from './interactionContext';
import { Block, Blocks } from '@chameleoncreator/creator-app';
import getBlockById from 'services/apiHelpers/getBlockById';
import IELayout from 'components/Layouts/IELayout';
import ResultsPageContent from './ResultsPageContent';
import ResultsPageMedia from './ResultsPageMedia';
import { useSelector } from 'react-redux';
import ResultsPageResult from './ResultsPageResult';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { getAllScoringInteractions } from 'services/scoring';

import { State } from 'store/store';
import { findBlocksIn } from 'selectors/block';
import { createSelector } from 'reselect';

const SingleColumnLayout = Row.bind({}) as any;
SingleColumnLayout.defaultProps = {
  ...(Row as any).defaultProps,
  flexDirection: 'column',
};

const TwoColumnLayout = Row.bind({}) as any;
TwoColumnLayout.defaultProps = {
  ...(Row as any).defaultProps,
  flexDirection: 'row',
};

const Components = {
  Row,
  Column,
  Text: EditableText,
  Image,
  TwoColumnLayout: TwoColumnLayout,
  SingleColumnLayout: SingleColumnLayout,
  ContinueButton: ContinueBtn,
  Video,
  SVG,
  Media,
  ResultsPageContent,
  ResultsPageContentPassed: ({ children }) => children[0],
  ResultsPageContentFailed: ({ children }) => children[0],
  ResultsPageResult,
  ResultsPageMedia,
  ...IELayout(interactionContext),
};

/**
 * activeBlockSelector finds blocks that are accessible from the root block
 */
const activeBlockSelector = (blocks: Blocks, rootId: string): Block[] => {
  return findBlocksIn(blocks, blocks[rootId], (i: any) => i);
};

/**
 * scoringBlocks finds all the blocks that are scorable and have it turned on
 */
const scoringBlocks = (blocks: Block[]): string[] => {
  return blocks
    .filter((b: any) => {
      return b?.props?.score && b.props.score === true;
    })
    .map((b: any) => b.id);
};

/**
 * This selector pulls out all the active blocks from the store
 */
const loadActiveBlocks = createSelector(
  [(state: State) => state.blocks, (state: State) => state.rootBlockId!],
  activeBlockSelector
);

const loadScoringBlocks = createSelector(loadActiveBlocks, scoringBlocks);

const getModuleBlock = createSelector(
  (state: State) => state.blocks,
  (blocks: Blocks) => Object.values(blocks).find(b => b.type === 'Module')
);

const ResultsPageDisplay = props => {
  const interactionBlock = getBlockById(props.blockId);

  const [displayPassFailContent, setDisplayPassFailContent] = useState(
    'passed'
  );

  const { scoreData = {} } = useSelector((state: State) => ({
    scoreData: state.userBehaviour.score,
  }));

  const moduleBlock = useSelector(getModuleBlock);
  let mbProps = { passMark: 80 };
  if (moduleBlock) {
    mbProps = moduleBlock.props as { passMark: number };
  }

  const passMark = mbProps.passMark;

  const scoringInteractions = useSelector(loadScoringBlocks);
  const numberOfScorableInteractions = scoringInteractions.length;

  const scoreRaw = Object.values(scoreData).reduce<number>(
    (acc, curr) => acc + curr,
    0
  );

  const scorePercent =
    ((scoreRaw || 0) / numberOfScorableInteractions) * 100 || 0;

  const hasScoringInteractions = !!getAllScoringInteractions().length;
  const passed = hasScoringInteractions
    ? scorePercent >= Number(passMark)
    : true;
  const score = hasScoringInteractions ? scorePercent.toFixed() : 100;

  const { init } = useIntersectionObserver(true);

  useEffect(() => {
    init([document.querySelector(`#i${interactionBlock.id}`)], {
      threshold: 0.5,
    });
  }, []);

  useEffect(() => {
    parent[`displayPassFailContent${interactionBlock.id}`] = passedFailed => {
      setDisplayPassFailContent(passedFailed);
    };
  });

  const container = (
    <Container
      id={interactionBlock.id}
      key={interactionBlock.id}
      {...interactionBlock.props}
      {...props}
    >
      <interactionContext.Provider
        value={{
          ...interactionBlock,
          displayPassFailContent,
          score,
          passed,
        }}
      >
        {(interactionBlock.children as Block[]).map(child => {
          return renderBlock(child, Components);
        })}
      </interactionContext.Provider>
    </Container>
  );

  return window.dev()
    ? container
    : useMemo(() => {
        // console.log('resultsPage renders');
        return container;
      }, [
        props.isOpen,
        displayPassFailContent,
        score,
        passed,
        interactionBlock.props!.lockDown,
      ]);
};

export default ResultsPageDisplay;
